/**
 * The template editor workspace: a two-pane view that fills a research-authored template into a finished
 * document. LEFT is the live preview — the template resolved against the answers as they stand, updating
 * on every keystroke, with filled values, unfilled gaps and LLM-drafted passages each visibly distinct.
 * RIGHT is the question column, grouped by document section, with every answer pre-filled where reeve
 * already knows or can sensibly suggest a value.
 *
 * Pure UI over a loaded bundle: the host page supplies the drafting and creation callbacks
 * (TemplateFillPage wires the real API; DevTemplatePage feeds it a static sample). Free-text passages
 * are drafted automatically (once, per passage, when first active) from the answers entered so far;
 * the user can edit or redraft.
 *
 * CONTRACT: answers seed ONCE per mount from the bundle's prefill. A host swapping to a different
 * bundle must remount (pass a `key` derived from slug + scope), or the new definition would resolve
 * against the old answers.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brand } from '../../components/Brand';
import { TemplatePreview } from './TemplatePreview';
import { resolveTemplate, type ActiveQuestion } from '../../lib/templateResolver';
import type { TemplateAnswers, TemplateBundle } from '../../lib/entities/templates';
import '../../styles/app-templates.css';

type DraftFlag = { drafting?: boolean; error?: string; attempted?: boolean; userEdited?: boolean };

export function TemplateEditor({
  bundle,
  backHref,
  onDraft,
  onCreate,
}: {
  bundle: TemplateBundle;
  /** Where the back link points (the document detail page). */
  backHref: string;
  /** Draft one passage (draftId without the `draft:` prefix) from the filled scalar values. */
  onDraft: (draftId: string, scalars: Record<string, string>) => Promise<{ text: string }>;
  /** Create the document from the completed answers; the host navigates away on success. */
  onCreate: (answers: TemplateAnswers) => Promise<void>;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => seedValues(bundle));
  const [repeats, setRepeats] = useState<Record<string, Record<string, string>[]>>({});
  const [draftFlags, setDraftFlags] = useState<Record<string, DraftFlag>>({});
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Latest values for async draft calls (avoids stale closures without re-running effects per keystroke).
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const cardRefs = useRef(new Map<string, HTMLDivElement>());

  // ── Live resolution ──────────────────────────────────────────────────────────────────────────────
  const resolved = useMemo(
    () => resolveTemplate(bundle.definition, values, repeats),
    [bundle, values, repeats],
  );

  // The set of ACTIVE draft keys, as a stable string so the auto-draft effect fires on membership
  // changes (an IF toggle revealing a new passage), not merely on count changes.
  const activeDraftKeys = useMemo(
    () => resolved.questions.filter((q) => q.kind === 'draft').map((q) => q.key).sort().join('|'),
    [resolved.questions],
  );

  // Flags read inside the auto-draft effect without depending on them (a flag write must not refire it).
  const draftFlagsRef = useRef(draftFlags);
  draftFlagsRef.current = draftFlags;

  // ── Auto-draft: each active passage is drafted once, grounded in the answers at that moment ──────
  useEffect(() => {
    const pending = activeDraftKeys
      .split('|')
      .filter(Boolean)
      .filter((key) => {
        const flag = draftFlagsRef.current[key];
        const text = valuesRef.current[key];
        return !flag?.attempted && !flag?.drafting && (!text || text.trim().length === 0);
      });
    if (pending.length === 0) return;

    // Mark attempted synchronously so a re-render cannot double-fire, then run sequentially: a burst
    // of parallel LLM calls on open would be slower for the user and harder on the provider.
    setDraftFlags((f) => {
      const next = { ...f };
      for (const key of pending) next[key] = { ...next[key], drafting: true, attempted: true };
      return next;
    });

    // CANCELLATION CONTRACT (a wedge lived here): `cancelled` only stops NEW work from starting.
    // Flag settlement is unconditional — an in-flight request that lands after cancellation still
    // clears its `drafting` flag (a post-unmount setState is a safe no-op in React 18), and every
    // pending key the loop never reached is reset to unattempted so the refired effect picks it up.
    // Skipping either write left passages on a permanent spinner with Create disabled forever.
    let cancelled = false;
    const remaining = new Set(pending);
    void (async () => {
      for (const key of pending) {
        if (cancelled) break;
        remaining.delete(key);
        const draftId = key.replace(/^draft:/, '');
        try {
          const { text } = await onDraft(draftId, scalarValues(valuesRef.current));
          setValues((v) => {
            // Never clobber something the user has typed in the meantime.
            const current = v[key];
            if (current && current.trim().length > 0) return v;
            return { ...v, [key]: text };
          });
          setDraftFlags((f) => ({ ...f, [key]: { ...f[key], drafting: false } }));
        } catch (err: unknown) {
          setDraftFlags((f) => ({
            ...f,
            [key]: {
              ...f[key],
              drafting: false,
              error: err instanceof Error ? err.message : 'Could not draft this passage.',
            },
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
      // Un-started keys go back to unattempted so a later membership change can draft them.
      setDraftFlags((f) => {
        if (remaining.size === 0) return f;
        const next = { ...f };
        for (const key of remaining) next[key] = { ...next[key], drafting: false, attempted: false };
        return next;
      });
    };
  }, [activeDraftKeys, onDraft]);

  const redraft = useCallback(
    async (key: string) => {
      const draftId = key.replace(/^draft:/, '');
      setDraftFlags((f) => ({ ...f, [key]: { ...f[key], drafting: true, error: undefined, attempted: true } }));
      try {
        const { text } = await onDraft(draftId, scalarValues(valuesRef.current));
        setValues((v) => ({ ...v, [key]: text }));
        setDraftFlags((f) => ({ ...f, [key]: { ...f[key], drafting: false, userEdited: false } }));
      } catch (err: unknown) {
        setDraftFlags((f) => ({
          ...f,
          [key]: {
            ...f[key],
            drafting: false,
            error: err instanceof Error ? err.message : 'Could not draft this passage.',
          },
        }));
      }
    },
    [onDraft],
  );

  // ── Focus hand-off: preview click scrolls + flashes the question card ────────────────────────────
  const focusQuestion = useCallback(
    (key: string) => {
      // A chip inside a register row carries the FIELD name, whose inputs live on the repeat's card —
      // fall back to the repeat question that owns the field so the click always lands somewhere.
      let targetKey = key;
      if (!cardRefs.current.has(key)) {
        const owner = resolved.questions.find(
          (q) => q.kind === 'repeat' && (q.itemFields ?? []).some((f) => f.name === key),
        );
        if (owner) targetKey = owner.key;
      }
      setFocusKey(targetKey);
      const card = cardRefs.current.get(targetKey);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = card.querySelector<HTMLElement>('input, textarea, button');
        input?.focus({ preventScroll: true });
      }
    },
    [resolved.questions],
  );

  const setValue = useCallback((key: string, value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
  }, []);

  // ── Create ───────────────────────────────────────────────────────────────────────────────────────
  const create = useCallback(async () => {
    setCreating(true);
    setCreateError(null);
    try {
      await onCreate({ values, repeats });
      // The real host navigates away inside onCreate, making this a no-op; a host that stays mounted
      // (the dev harness) gets its buttons back. Double-fire is impossible either way: the button is
      // disabled for the whole await, and navigation has already happened when the host leaves.
      setCreating(false);
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Could not create the document.');
      setCreating(false);
    }
  }, [onCreate, values, repeats]);

  // ── Render ───────────────────────────────────────────────────────────────────────────────────────

  const missingCount = resolved.missing.length;
  const answerable = resolved.questions.filter((q) => q.kind !== 'repeat').length;
  const ready = missingCount === 0;

  // The question column, grouped by document section in document order.
  const grouped: { section: string; questions: ActiveQuestion[] }[] = [];
  for (const q of resolved.questions) {
    const last = grouped[grouped.length - 1];
    if (last && last.section === (q.section || 'Details')) last.questions.push(q);
    else grouped.push({ section: q.section || 'Details', questions: [q] });
  }

  return (
    <div className="apage apage--tplfill">
      <header className="tplf-head">
        <div className="tplf-head__lead">
          <Link to={backHref} className="tplf-back">
            ← {bundle.definition.title}
          </Link>
          <h1 className="tplf-title">Fill in your {bundle.definition.title.toLowerCase()}</h1>
          <p className="tplf-sub">
            Answer on the right, watch the document build on the left. <Brand /> has pre-filled what it
            can; everything stays editable. Compliance information, not legal advice.
          </p>
        </div>
        <div className="tplf-head__actions">
          <span className={`tplf-count${ready ? ' is-ready' : ''}`}>
            {ready ? 'Ready to create' : `${missingCount} of ${answerable} still to fill in`}
          </span>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!ready || creating}
            onClick={create}
          >
            {creating ? 'Creating…' : 'Create document'}
          </button>
        </div>
      </header>
      {createError && <p className="tplf-error">{createError}</p>}

      <div className="tplf-split">
        <section className="tplf-preview" aria-label="Document preview">
          <TemplatePreview
            blocks={resolved.blocks}
            meta={resolved.meta}
            focusKey={focusKey}
            onFocus={focusQuestion}
          />
        </section>

        <aside className="tplf-questions" aria-label="Questions">
          {grouped.map((group) => (
            <section key={group.section} className="tplf-group">
              <h2 className="tplf-group__title">{group.section}</h2>
              {group.questions.map((q) => (
                <QuestionCard
                  key={q.key}
                  q={q}
                  value={values[q.key] ?? ''}
                  rows={repeats[q.key] ?? []}
                  bundle={bundle}
                  flag={draftFlags[q.key]}
                  focused={focusKey === q.key}
                  refFn={(el) => {
                    if (el) cardRefs.current.set(q.key, el);
                    else cardRefs.current.delete(q.key);
                  }}
                  onFocus={() => setFocusKey(q.key)}
                  onValue={(v) => setValue(q.key, v)}
                  onUserEditDraft={() =>
                    setDraftFlags((f) => ({ ...f, [q.key]: { ...f[q.key], userEdited: true } }))
                  }
                  onRedraft={() => void redraft(q.key)}
                  onRows={(rows) => setRepeats((r) => ({ ...r, [q.key]: rows }))}
                />
              ))}
            </section>
          ))}
          <div className="tplf-colfoot">
            <button
              type="button"
              className="btn btn-primary btn-block"
              disabled={!ready || creating}
              onClick={create}
            >
              {creating ? 'Creating…' : ready ? 'Create document' : `${missingCount} still to fill in`}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

/**
 * Seed the answers: booleans start "no" (progressive disclosure), choices on their first branch — the
 * same defaults the backend resolver applies to absent keys, so preview and document agree — then the
 * server-merged prefill on top.
 */
function seedValues(bundle: TemplateBundle): Record<string, string> {
  const seed: Record<string, string> = {};
  const walk = (nodes: TemplateBundle['definition']['body']) => {
    for (const n of nodes) {
      if (n.t === 'if') {
        seed[`if:${n.m}`] = 'false';
        walk(n.c ?? []);
      } else if (n.t === 'choose') {
        for (const br of n.c ?? []) walk(br.c ?? []);
      } else if (n.t === 'locked' || n.t === 'repeat' || n.t === 'branch') {
        walk(n.c ?? []);
      }
    }
  };
  walk(bundle.definition.body);
  for (const [k, v] of Object.entries(bundle.prefill)) seed[k] = v;
  return seed;
}

/** The filled scalar answers (no if:/choice:/draft: keys), for grounding a draft. */
function scalarValues(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    if (!k.includes(':') && v.trim().length > 0) out[k] = v;
  }
  return out;
}

// ── One question card ────────────────────────────────────────────────────────────────────────────────

function QuestionCard({
  q,
  value,
  rows,
  bundle,
  flag,
  focused,
  refFn,
  onFocus,
  onValue,
  onUserEditDraft,
  onRedraft,
  onRows,
}: {
  q: ActiveQuestion;
  value: string;
  rows: Record<string, string>[];
  bundle: TemplateBundle;
  flag?: DraftFlag;
  focused: boolean;
  refFn: (el: HTMLDivElement | null) => void;
  onFocus: () => void;
  onValue: (v: string) => void;
  onUserEditDraft: () => void;
  onRedraft: () => void;
  onRows: (rows: Record<string, string>[]) => void;
}) {
  // Source chip: shown while the value is still exactly what reeve pre-filled; an edit makes it theirs.
  const source = bundle.sources[q.key];
  const untouched = value === (bundle.prefill[q.key] ?? '');
  const chip =
    source && untouched && value.length > 0
      ? source === 'suggested'
        ? 'suggested'
        : source === 'saved'
          ? 'saved earlier'
          : 'from your profile'
      : null;

  const classes = `tplq${focused ? ' is-focus' : ''}${value.trim().length === 0 && q.kind !== 'boolean' && q.kind !== 'choice' && q.kind !== 'repeat' ? ' is-missing' : ''}`;

  if (q.kind === 'boolean') {
    return (
      <div className={classes} ref={refFn} onFocusCapture={onFocus}>
        <span className="tplq-label">{q.prompt}</span>
        <div className="tplq-yesno">
          <button
            type="button"
            className={`btn btn-sm ${value === 'true' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onValue('true')}
          >
            Yes
          </button>
          <button
            type="button"
            className={`btn btn-sm ${value === 'false' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onValue('false')}
          >
            No
          </button>
        </div>
      </div>
    );
  }

  if (q.kind === 'choice') {
    return (
      <div className={classes} ref={refFn} onFocusCapture={onFocus}>
        <span className="tplq-label">{q.prompt}</span>
        <div className="tplq-choices">
          {(q.options ?? []).map((opt, i) => {
            const selected = value === String(i) || (value === '' && i === 0);
            return (
              <button
                key={i}
                type="button"
                className={`tplq-choice${selected ? ' is-selected' : ''}`}
                onClick={() => onValue(String(i))}
              >
                <span className="tplq-choice__radio" aria-hidden="true" />
                <span className="tplq-choice__body">
                  <span className="tplq-choice__label">{opt.label}</span>
                  {opt.preview && opt.preview !== opt.label && (
                    <span className="tplq-choice__preview">{opt.preview}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (q.kind === 'draft') {
    return (
      <div className={classes} ref={refFn} onFocusCapture={onFocus}>
        <span className="tplq-label">
          {q.prompt}
          <span className="tplq-chip tplq-chip--draft">written for you</span>
        </span>
        {flag?.drafting ? (
          <p className="tplq-drafting">
            <span className="tplq-spin" aria-hidden="true" /> Writing a first draft…
          </p>
        ) : (
          <>
            {flag?.error && <p className="tplq-error">{flag.error}</p>}
            <textarea
              className="textarea tplq-drafttext"
              value={value}
              placeholder="reeve will draft this from your answers, or write it yourself."
              onChange={(e) => {
                onUserEditDraft();
                onValue(e.target.value);
              }}
            />
            <div className="tplq-draftrow">
              <button type="button" className="tplq-link" onClick={onRedraft}>
                {value.trim().length > 0 ? 'Redraft with reeve' : 'Draft with reeve'}
              </button>
              {q.help && <GuidanceToggle help={q.help} />}
            </div>
          </>
        )}
      </div>
    );
  }

  if (q.kind === 'repeat') {
    return (
      <div className={classes} ref={refFn} onFocusCapture={onFocus}>
        <span className="tplq-label">{q.prompt}</span>
        {rows.map((row, i) => (
          <div className="tplq-row" key={i}>
            <div className="tplq-row__head">
              <span>Row {i + 1}</span>
              <button type="button" className="tplq-link" onClick={() => onRows(rows.filter((_, ri) => ri !== i))}>
                Remove
              </button>
            </div>
            {(q.itemFields ?? []).map((f) => (
              <label className="tplq-sub" key={f.name}>
                <span className="tplq-sublabel">{f.label}</span>
                <input
                  className="input"
                  type="text"
                  value={row[f.name] ?? ''}
                  placeholder={f.example}
                  onChange={(e) => onRows(rows.map((r, ri) => (ri === i ? { ...r, [f.name]: e.target.value } : r)))}
                />
              </label>
            ))}
          </div>
        ))}
        <button type="button" className="btn btn-secondary btn-xs" onClick={() => onRows([...rows, {}])}>
          {rows.length === 0 ? 'Add a row' : 'Add another'}
        </button>
      </div>
    );
  }

  // A plain field.
  const multiline = q.kind === 'textarea';
  return (
    <div className={classes} ref={refFn} onFocusCapture={onFocus}>
      <label className="tplq-field">
        <span className="tplq-label">
          {q.prompt}
          {chip && <span className={`tplq-chip${source === 'suggested' ? ' tplq-chip--suggested' : ''}`}>{chip}</span>}
        </span>
        {q.help && <span className="tplq-help">{q.help}</span>}
        {multiline ? (
          <textarea
            className="textarea"
            value={value}
            placeholder={q.example ?? ''}
            onChange={(e) => onValue(e.target.value)}
          />
        ) : (
          <input
            className="input"
            type="text"
            value={value}
            placeholder={q.example ?? ''}
            onChange={(e) => onValue(e.target.value)}
          />
        )}
      </label>
    </div>
  );
}

function GuidanceToggle({ help }: { help: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="tplq-link" onClick={() => setOpen((o) => !o)}>
        {open ? 'Hide guidance' : 'What to write'}
      </button>
      {open && <div className="tplq-guidance">{help}</div>}
    </>
  );
}
